import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

import { parseAtom } from "@/lib/parse-atom"
import { parseJupyter } from "@/lib/parse-jupyter"
import { parseSublimeText } from "@/lib/parse-sublime-text"
import { parseVSCode } from "@/lib/parse-vs-code"
import { parseZed } from "@/lib/parse-zed"

import { FormValues } from "./schema"
import { FormatKey } from "./types"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

function wrapWithBraces(content: string) {
  const trimmed = content.trim()
  return `{\n${trimmed}\n}`
}

function wrapWithScope(content: string) {
  const scoped = content
    .trim()
    .split("\n")
    .map((line) => `  ${line}`)
    .join("\n")
  return `.source.js:\n${scoped}\n`
}

function formatJsonString(content: string) {
  try {
    const parsed = JSON.parse(content)
    return JSON.stringify(parsed, null, 2)
  } catch {
    return content
  }
}

export const formatDefinitions: Record<
  FormatKey,
  { label: string; extension: string; build: (values: FormValues) => string }
> = {
  vscode: {
    label: "VS Code",
    extension: "code-snippets",
    build: (values) =>
      formatJsonString(
        wrapWithBraces(
          parseVSCode(
            values.description,
            values.tabTrigger,
            values.snippet,
            values.scope
          )
        )
      ),
  },
  sublime: {
    label: "Sublime Text",
    extension: "sublime-snippet",
    build: (values) =>
      parseSublimeText(values.description, values.tabTrigger, values.snippet),
  },
  atom: {
    label: "Atom",
    extension: "cson",
    build: (values) =>
      wrapWithScope(
        parseAtom(values.description, values.tabTrigger, values.snippet)
      ),
  },
  zed: {
    label: "Zed",
    extension: "json",
    build: (values) =>
      formatJsonString(
        wrapWithBraces(
          parseZed(values.description, values.tabTrigger, values.snippet)
        )
      ),
  },
  jupyter: {
    label: "Jupyter",
    extension: "json",
    build: (values) =>
      parseJupyter(values.description, values.tabTrigger, values.snippet),
  },
}
